import { Injectable } from '@angular/core';

export interface ViewportPosition {
  x: number;
  y: number;
}

@Injectable({
  providedIn: 'root'
})
export class ChatDomEffectsService {
  focusById(id: string): void {
    const input = document.getElementById(id) as HTMLInputElement | null;
    input?.focus();
  }

  focusByIdDeferred(id: string): void {
    setTimeout(() => this.focusById(id), 0);
  }

  focusInputAtEnd(id: string): void {
    const input = document.getElementById(id) as HTMLInputElement | null;
    input?.focus();
    if (!input) {
      return;
    }

    const length = input.value.length;
    input.setSelectionRange(length, length);
  }

  async copyText(text: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }

  scrollElementById(id: string, options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'nearest' }): void {
    document.getElementById(id)?.scrollIntoView(options);
  }

  scrollElementByIdDeferred(
    id: string,
    delayMs: number,
    options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'nearest' }
  ): void {
    setTimeout(() => this.scrollElementById(id, options), delayMs);
  }

  scrollElement(element: HTMLElement | null | undefined, options: ScrollIntoViewOptions): void {
    element?.scrollIntoView(options);
  }

  scrollToBottom(container: HTMLElement | null | undefined): void {
    const doScroll = () => {
      if (!container) {
        return;
      }

      const last = container.lastElementChild as HTMLElement | null;
      if (last && typeof last.scrollIntoView === 'function') {
        try {
          last.scrollIntoView({ behavior: 'auto', block: 'end' });
        } catch {
          container.scrollTop = container.scrollHeight;
        }
        return;
      }

      container.scrollTop = container.scrollHeight;
    };

    doScroll();
    requestAnimationFrame(doScroll);
    setTimeout(doScroll, 50);
  }

  addBodyClass(className: string): void {
    document.body.classList.add(className);
  }

  removeBodyClass(className: string): void {
    document.body.classList.remove(className);
  }

  reload(): void {
    window.location.reload();
  }

  getClampedPosition(event: MouseEvent | undefined, width: number, height: number, offset = 8): ViewportPosition {
    if (!event) {
      return { x: 100, y: 100 };
    }

    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);

    return {
      x: Math.min(event.clientX + offset, Math.max(8, vw - width)),
      y: Math.min(event.clientY + offset, Math.max(8, vh - height))
    };
  }
}
